import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';

import { useFetch } from '../../../hooks/use-fetch';
import { useSpaceContext } from '../../../hooks/use-space-context';
import { useAnalyticsContext } from '../../../providers/analytics-provider';
import {
  fetchAdminEventUtilization,
  fetchAdminLicenseConsumption,
} from '../../../services/endpoints';

type IEventTypeBreakdownEntry = {
  count: number;
  percentage: number;
};

type IEventUtilizationResponse = {
  events_created: number;
  events_hosted: number;
  series_count: number;
  event_type_breakdown: {
    virtual?: IEventTypeBreakdownEntry;
    webinar?: IEventTypeBreakdownEntry;
    hybrid?: IEventTypeBreakdownEntry;
    onsite?: IEventTypeBreakdownEntry;
  };
};

type ILicenseConsumptionResponse = {
  active_users: number;
  active_users_percentage: number;
  used_seats: number;
  total_seats: number;
  is_unlimited: boolean;
  usage_percentage: number;
};

export type LicenseConsumptionData = {
  activeOrganizers: number;
  activeOrganizersPercentage: number;
  usedSeats: number;
  totalSeats: number;
  isUnlimited: boolean;
  usagePercentage: number;
};

export type EventUtilizationData = {
  eventsCreated: number;
  hosted: number;
  series: number;
  eventTypeDistribution: {
    webinar: number;
    virtual: number;
    hybrid: number;
    onSite: number;
  };
};

type UseKPIReturn<T> = {
  data: T | null;
  isLoading: boolean;
  isError: boolean;
};

const transformLicenseConsumption = (
  raw: ILicenseConsumptionResponse,
): LicenseConsumptionData => {
  if (
    typeof raw?.active_users !== 'number' ||
    typeof raw?.active_users_percentage !== 'number' ||
    typeof raw?.used_seats !== 'number' ||
    typeof raw?.total_seats !== 'number' ||
    typeof raw?.is_unlimited !== 'boolean' ||
    typeof raw?.usage_percentage !== 'number'
  ) {
    throw new Error('Invalid license consumption response');
  }

  return {
    activeOrganizers: raw.active_users,
    activeOrganizersPercentage: Math.round(raw.active_users_percentage),
    usedSeats: raw.used_seats,
    totalSeats: raw.total_seats,
    isUnlimited: raw.is_unlimited,
    usagePercentage: Math.round(raw.usage_percentage),
  };
};

const transformEventUtilization = (
  raw: IEventUtilizationResponse,
): EventUtilizationData => ({
  eventsCreated: raw.events_created,
  hosted: raw.events_hosted,
  series: raw.series_count,
  eventTypeDistribution: {
    webinar: Math.round(raw.event_type_breakdown.webinar?.percentage ?? 0),
    virtual: Math.round(raw.event_type_breakdown.virtual?.percentage ?? 0),
    hybrid: Math.round(raw.event_type_breakdown.hybrid?.percentage ?? 0),
    onSite: Math.round(raw.event_type_breakdown.onsite?.percentage ?? 0),
  },
});

export const useLicenseConsumption =
  (): UseKPIReturn<LicenseConsumptionData> => {
    const orgId: string = useReadDashboardSharedState('org_id') || '';
    const { spaceExternalId } = useSpaceContext();

    const { data, isLoading, isError } = useFetch(
      {},
      `admin-license-consumption-${orgId}-${spaceExternalId}`,
      query => {
        const params = new URLSearchParams(query);
        if (spaceExternalId) params.set('space_external_id', spaceExternalId);
        return fetchAdminLicenseConsumption(orgId, params.toString());
      },
      transformLicenseConsumption,
    );

    return { data: data ?? null, isLoading, isError };
  };

export const useEventUtilization = (): UseKPIReturn<EventUtilizationData> => {
  const { state } = useAnalyticsContext();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { dateRange, startDate, endDate } = state;
  const { spaceExternalId } = useSpaceContext();

  const { data, isLoading, isError } = useFetch(
    { dateRange, startDate, endDate },
    `admin-event-utilization-${orgId}-${spaceExternalId}`,
    query => {
      const params = new URLSearchParams(query);
      if (spaceExternalId) params.set('space_external_id', spaceExternalId);
      return fetchAdminEventUtilization(orgId, params.toString());
    },
    transformEventUtilization,
  );

  return { data: data ?? null, isLoading, isError };
};
